<template>
  <v-menu
    v-model="dialogOpen"
    offset-x
    left
    :close-on-content-click="false"
    :max-width="300"
  >
    <template v-slot:activator="{ on, attrs }">
      <slot name="activator" :attrs="attrs" :on="on" />
    </template>

    <v-card>
      <v-card-text class="pb-0 form_container">
        <div class="fields-container">
          <v-autocomplete
            v-model="innerValue.responsaveis"
            clearable
            dense
            hide-details
            :items="responsaveis"
            item-text="label"
            item-value="id"
            label="Responsaveis"
            multiple
            outlined
          />
          <div class="field-label">ou</div>
          <v-autocomplete
            v-model="innerValue.business_units"
            clearable
            dense
            hide-details
            :items="bus"
            item-text="label"
            item-value="id"
            label="Diretoria"
            multiple
            outlined
          />
          <div class="field-label">e</div>
          <v-autocomplete
            v-model="innerValue.status_list"
            clearable
            dense
            hide-details
            :items="statusOptions"
            item-text="label"
            item-value="id"
            label="Status"
            multiple
            outlined
          />
          <div class="field-label">e</div>
          <v-autocomplete
            v-model="innerValue.business_plan_id"
            :append-icon="businessPlansAppendIcon"
            :class="{ 'loading-animation': loadingFiltersOpts }"
            clearable
            dense
            hide-details
            :items="bps"
            item-text="label"
            item-value="id"
            label="Business Plan"
            outlined
            :placeholder="businessPlansPlaceholder"
            @click="getSelection()"
          />
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="px-6 pb-3 pt-0">
        <v-spacer></v-spacer>
        <v-btn color="secondary" class="px-5" dark @click.stop="doClear()">
          Limpar
        </v-btn>
        <v-btn color="primary" class="px-5" depressed @click.stop="doFilter()">
          Filtrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import yearsOptions from "@/helpers/yearsOptions";
import { mapGetters } from "vuex";
import { cloneDeep, isEmpty } from "lodash";
import getDifferences from "@/helpers/getDifferences";

export const createEmptyFilters = () => ({
  business_units: [],
  business_plan_id: null,
  responsaveis: [],
  status_list: []
});

export default {
  props: {
    open: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      bus: [],
      bps: [],
      businessPlansUpToDate: false,
      dialogOpen: false,
      responsaveis: [],
      innerValue: createEmptyFilters(),
      loadingFiltersOpts: false,
      previousValue: {},
      statusOptions: [],
      yearsOptions
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    businessPlansAppendIcon() {
      return this.loadingFiltersOpts ? "mdi-loading" : "$dropdown";
    },
    businessPlansPlaceholder() {
      return this.loadingFiltersOpts ? "Carregando..." : "Todos";
    },
    filtersHasChanged() {
      const {
        business_units: prevBUs = [],
        responsaveis: prevRes = [],
        status_list: prevStatus = [],
      } = this.previousValue;
      const {
        business_units: curBUs = [],
        responsaveis: curRes = [],
        status_list: curStatus = [],
      } = this.innerValue;
      const hasDiff =
        prevBUs.length != curBUs.length ||
        !isEmpty(getDifferences(prevBUs, curBUs)) ||
        prevRes.length != curRes.length ||
        !isEmpty(getDifferences(prevRes, curRes))
        prevStatus.length != curStatus.length ||
        !isEmpty(getDifferences(prevStatus, curStatus));

      return hasDiff;
    }
  },
  async created() {
    await this.getSelection(true, true);
    this.doFilter();
  },
  watch: {
    dialogOpen(value) {
      this.$emit("update:open", !!value);
    },
    // Se os campos Responsaveis, Diretoria ou Status forem alterados
    // avisa que precisa atualizar lista de bps
    filtersHasChanged(value) {
      this.businessPlansUpToDate = value === this.businessPlansUpToDate ? !value : this.businessPlansUpToDate;
    },
    open(value) {
      this.dialogOpen = value;
    },
    innerValue(value) {
      this.$emit("change", value);
    }
  },
  methods: {
    doClear() {
      this.previousValue = {};
      this.innerValue = createEmptyFilters();
      this.getSelection(true);
      this.$emit("clear");
    },
    async doFilter() {
      try {
        const data = cloneDeep(this.innerValue);

        if (!Array.isArray(data.business_plans) || data.business_plans.length === 0) {
          // Atualiza BPs (se precisar)
          await this.getSelection(false, true);
          data.business_plans = this.bps.map(({ id }) => id);
        }

        this.$emit("filter", data);
        this.dialogOpen = false;
      } catch (error) {
        this.notify(error);
      }
    },async getSelection(force = false, globalLoading = false) {
      const PROCESS_NAME = "dashboard-bp-filters-selection-loading";

      try {
        if (!force && this.businessPlansUpToDate) {
          return;
        }

        if (globalLoading) {
          this.$root.$emit("addLoadingProcess", PROCESS_NAME);
        }

        this.loadingFiltersOpts = true;

        const {
          business_units,
          responsaveis,
          status_list
        } = this.innerValue;
        const params = [];

        if (Array.isArray(business_units) && business_units.length > 0) {
          params.push(`filters[business_units]=${business_units.join(";")}`);
        }

        if (Array.isArray(responsaveis) && responsaveis.length > 0) {
          params.push(`filters[responsaveis]=${responsaveis.join(";")}`);
        }

        if (Array.isArray(status_list) && status_list.length > 0) {
          params.push(`filters[status_list]=${status_list.join(";")}`);
        }

        const query = params.length > 0 ? params.join("&") : "";

        this.previousValue = cloneDeep(this.innerValue);
        const { data: response } = await this.api().get(
          `v1/ativacaoped/${this.clientId}/dashboard/selecao?${query}`
        );
        this.bus = response.businessUnits
          .map(bu => ({
            ...bu,
            label: `${bu.nome} (${bu.divisao})`
          }))
          .sort(({ label: a }, { label: b }) => a.localeCompare(b));
        this.bps = response.businessPlans
          .map(bp => ({
            ...bp,
            label: `${bp.nome} (${bp.versao_label})`
          }))
          .sort(({ label: a }, { label: b }) => a.localeCompare(b));
        this.responsaveis = response.responsaveis
          .map(responsavel => ({
            ...responsavel,
            label: `${responsavel.nome} (${responsavel.email})`
          }))
          .sort(({ label: a }, { label: b }) => a.localeCompare(b));

        this.$emit("beginYear", response.anoBase.inicio || 0);
        this.$emit("endYear", response.anoBase.fim || 0);

        this.businessPlansUpToDate = true;
      } catch (error) {
        this.notify(error);
        this.bps = [];
      } finally {
        this.loadingFiltersOpts = false;
        this.$root.$emit("removeLoadingProcess", PROCESS_NAME);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.v-card__text .fields-container {
  display: flex;
  flex-direction: column;

  .field-label {
    text-align: center;
  }

  .loading-animation::v-deep .theme--light.v-icon.v-icon.v-icon--disabled {
    @keyframes rotacao {
      from {
        transform: rotate(0deg);
      }

      to {
        transform: rotate(360deg);
      }
    }

    animation: rotacao 1s linear infinite;
    color: #215f9a !important;
  }
}
</style>
